// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import SectionWrapper, { SectionWrapperStyles } from "@flip/App/Components/Common/SectionWrapper";
import XngCarousel from "@flip/App/Components/XngCarousel/XngCarousel";
import { CustomAppState } from "@flip/App/Store/Reducers";
import InfoCard from "@flip/App/Widgets/InfoCards/InfoCard";
import { xScreen } from "@flip/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

interface InfoCardFields {
    fields: {
        title: string;
        buttonText: string;
        description: string;
        buttonUrl: LinkFieldValue;
        imageUrl: string;
        imageCaption: string;
        openInNewTab: boolean;
    };
}

const enum fields {
    title = "title",
    cards = "cards",
    buttonAlignment = "buttonAlignment",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.cards]: InfoCardFields[];
        [fields.buttonAlignment]: "left" | "center" | "right";
    };
}

type Props = OwnProps;

interface InfoCardsWidgetStyles {
    container?: InjectableCss;
    content?: GridItemProps;
    sectionWrapperStyles?: SectionWrapperStyles;
}

export const infoCardsWidgetStyles: InfoCardsWidgetStyles = {
    container: {
        css: css`
            padding-top: 0;
            gap: 22px;
            height: 100%;
            display: flex;
            flex-wrap: wrap;
            ${xScreen("lte", "md")} {
                gap: 24px 20px;
            }
        `,
    },
    content: {
        css: css`
            ${({ numberOfCards }: any) => {
                if (numberOfCards >= 3) {
                    return css`
                        width: calc((100% - 44px) / 3);
                        ${xScreen("lte", "md")} {
                            width: calc(50% - 10px);
                        }
                    `;
                    // eslint-disable-next-line no-else-return
                } else if (numberOfCards === 2) {
                    return css`
                        width: calc((100% - 22px) / 2);
                        ${xScreen("lte", "md")} {
                            width: calc(50% - 10px);
                        }
                    `;
                } else {
                    return css`
                        width: 100%;
                    `;
                }
            }}
            ${xScreen("lte", "xs")} {
                width: 100%;
            }
        `,
    },
    sectionWrapperStyles: {
        wrapper: {
            css: css`
                & > div {
                    height: 100%;
                }
            `,
        },
    },
};

const InfoCardsWidget: React.FC<Props> = ({ fields }) => {
    const textAlignStyles = `text-align: ${fields.buttonAlignment};`;

    if (!fields.cards || fields.cards.length === 0) {
        return null;
    }

    const { isSmallMobileScreen } = useAppSelector(state => {
        return {
            isSmallMobileScreen: (state as CustomAppState).xCommon.isSmallMobileScreen,
        };
    });

    const styles = infoCardsWidgetStyles;
    const styledProps = { numberOfCards: fields.cards.length };
    const cards = fields.cards.map((card, index) => (
        <React.Fragment key={index.toString()}>
            <StyledWrapper {...styles.content} {...styledProps}>
                <InfoCard
                    buttonText={card.fields.buttonText}
                    title={card.fields.title}
                    buttonUrl={card.fields.buttonUrl}
                    imageUrl={card.fields.imageUrl}
                    imageCaption={card.fields.imageCaption}
                    openInNewTab={card.fields.openInNewTab}
                    buttonAlignStyles={textAlignStyles}
                />
            </StyledWrapper>
        </React.Fragment>
    ));

    return (
        <SectionWrapper
            title={fields.title}
            extendedStyles={styles.sectionWrapperStyles}
            data-test-selector="Flip-InfoCardsWidget"
        >
            {isSmallMobileScreen ? (
                <XngCarousel navigation={true}>{cards}</XngCarousel>
            ) : (
                <StyledWrapper {...styles.container} className="infoCardsWrapper">
                    {cards}
                </StyledWrapper>
            )}
        </SectionWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: InfoCardsWidget,
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            // XNG-Changes: changed isRequired and getDisplay
            {
                name: fields.cards,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title || "Info Card";
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "imageUrl",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "imageCaption",
                        displayName: "Image Caption",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "title",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                        maxLength: 40,
                    },
                    {
                        name: "buttonText",
                        displayName: "Button Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "buttonUrl",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isRequired: true,
                        validate: (link: LinkFieldValue) => {
                            if (!link || !link.value) {
                                return "A value must be provided.";
                            }
                            return null;
                        },
                    },
                    {
                        name: "openInNewTab",
                        displayName: "Open in New Tab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
            {
                name: fields.buttonAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "right",
                fieldType: "General",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                hideEmptyOption: true,
            },
        ],
    },
};

export default widgetModule;
